import { useFormContext } from "react-hook-form";
import { FieldProps } from ".";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { Theme } from "@mui/system";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { CheckboxField as ReusableCheckboxField } from "@flightright/reusable-components";
import { useEffect, useState } from "react";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";
import { HOTJAR_ATTRIBUTES as extraProps } from "@/core/data";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";
import getFlatFormValue from "@/core/utils/getFlatFormValue";

const styles = (theme: Theme) => ({
  label: {
    display: "flex",
    alignItems: "flex-start",
    height: "100%",
    backgroundColor: "unset",
    minHeight: "auto",
    "&, & .Mui-checked, &:hover, &:has(.Mui-checked)": {
      border: "none",
    },
    "& span.MuiTypography-root": {
      paddingTop: "1.5px",
      fontSize: "14px",
    },
  },
  checkbox: {
    padding: 0,
    marginRight: theme.spacing(1),
    "& .MuiSvgIcon-root": {
      width: "24px",
      height: "24px",
    },
    "&.Mui-checked": {
      color: theme.palette.primary.main,
    },
    color: theme.palette.grey[800],
  },
});

const Checkbox = ({ field, initialValue }: FieldProps) => {
  const {
    control,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext();
  const [checked, setChecked] = useState<boolean>(false);
  const formValues = watch();

  useEffect(() => {
    const fieldValue = getFlatFormValue(formValues, field.key);
    if (fieldValue !== undefined) {
      setChecked(fieldValue);
    }
  }, [formValues]);

  const classes: any = useCustomStyles(styles);

  const { defaultValue } = field;
  const initiallyChecked: boolean | null = useDefaultValue(
    initialValue ?? defaultValue
  );

  useEffect(() => {
    if (initiallyChecked !== null) {
      setValue(field.key, initiallyChecked);
    }
  }, [initiallyChecked]);

  const { hasError, errorMessage, errorTranslationKey } = useFieldError(
    errors,
    field
  );

  return (
    <ReusableCheckboxField
      id={field.key}
      control={control}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: t(`validate.${errorTranslationKey}.required.message`),
        },
      }}
      checked={checked}
      label={t(field.label)}
      labelStyles={{ ...classes?.label }}
      checkboxStyles={{ ...classes?.checkbox }}
      error={hasError}
      errorMessage={errorMessage}
      {...extraProps}
    />
  );
};

export default Checkbox;
